/**
 * Smart Import — typed fetch wrappers for /api/import/*
 *
 * Contract mirrors FORK-butler-buddy-smart-import-backend-2026-04-17.md §
 * "Interface contract". The peer backend fork owns the actual routes; this
 * file is the single place the portal talks to them. All calls rely on the
 * HttpOnly butler_session cookie the rest of the portal uses — there is no
 * fetchWithAuth helper in this codebase, matching Dashboard.tsx's pattern.
 */

export interface ColumnInfo {
  index: number
  header: string
  sampleValues: string[]
  numericRatio: number
  avgLength: number
}

export interface UploadResult {
  jobId: string
  columns: ColumnInfo[]
  detectedBarcodeColumn: number | null
  rowCount: number
  detectedConfidence: 'high' | 'medium' | 'low'
}

export interface InvalidRow {
  row: number
  value: string
  reason: string
}

export interface ColumnConfirmResult {
  barcodeCount: number
  duplicates: number
  invalid: InvalidRow[]
  preview: string[]
}

export interface MmOption {
  id: number
  name: string
}

export interface ConfirmResult {
  success: boolean
  message: string
  mmImportUrl: string
}

export interface MmSession {
  connected: boolean
  connectedAt?: number
}

export interface MmConnectBody {
  auth: string
  session: string
  alb?: string
}

export type JobStatus =
  | 'uploaded'
  | 'column-confirmed'
  | 'event-picked'
  | 'plan-picked'
  | 'submitted'
  | 'failed'

export interface JobRecord {
  id: string
  status: JobStatus
  upload?: UploadResult
  confirm?: ColumnConfirmResult
  eventId?: number
  planId?: number
  result?: ConfirmResult
  error?: string
}

class ImportApiError extends Error {
  status: number
  constructor(status: number, message: string) {
    super(message)
    this.status = status
    this.name = 'ImportApiError'
  }
}

async function readError(res: Response): Promise<string> {
  try {
    const data = await res.json()
    if (typeof data?.error === 'string') return data.error
    if (typeof data?.message === 'string') return data.message
  } catch {
    /* not JSON */
  }
  return `HTTP ${res.status}`
}

async function jsonFetch<T>(
  url: string,
  init: RequestInit = {},
): Promise<T> {
  const res = await fetch(url, {
    credentials: 'same-origin',
    ...init,
    headers: {
      ...(init.body && !(init.body instanceof FormData)
        ? { 'Content-Type': 'application/json' }
        : {}),
      ...(init.headers ?? {}),
    },
  })
  if (!res.ok) throw new ImportApiError(res.status, await readError(res))
  return (await res.json()) as T
}

export const importApi = {
  getMmSession: () => jsonFetch<MmSession>('/api/import/mm-session'),

  mmConnect: (body: MmConnectBody) =>
    jsonFetch<{ ok: boolean; error?: string }>('/api/import/mm-connect', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  mmDisconnect: () =>
    jsonFetch<{ ok: true }>('/api/import/mm-disconnect', { method: 'POST' }),

  uploadFile: (file: File): Promise<UploadResult> => {
    const form = new FormData()
    form.append('file', file)
    return jsonFetch<UploadResult>('/api/import/jobs', {
      method: 'POST',
      body: form,
    })
  },

  confirmColumn: (jobId: string, columnIndex: number) =>
    jsonFetch<ColumnConfirmResult>(
      `/api/import/jobs/${encodeURIComponent(jobId)}/column`,
      {
        method: 'POST',
        body: JSON.stringify({ columnIndex }),
      },
    ),

  getEvents: (jobId: string) =>
    jsonFetch<MmOption[]>(
      `/api/import/jobs/${encodeURIComponent(jobId)}/events`,
    ),

  getPlans: (jobId: string, eventId: number) =>
    jsonFetch<MmOption[]>(
      `/api/import/jobs/${encodeURIComponent(jobId)}/events/${encodeURIComponent(
        String(eventId),
      )}/plans`,
    ),

  confirmJob: (
    jobId: string,
    body: {
      name: string
      eventId: number
      planId: number
      isAutoRedeemed?: boolean
      allowOverwriteForDuplicate?: boolean
    },
  ) =>
    jsonFetch<ConfirmResult>(
      `/api/import/jobs/${encodeURIComponent(jobId)}/confirm`,
      {
        method: 'POST',
        body: JSON.stringify(body),
      },
    ),

  getJob: (jobId: string) =>
    jsonFetch<JobRecord>(`/api/import/jobs/${encodeURIComponent(jobId)}`),
}

export { ImportApiError }
